import * as React from 'react';
import { useHistory } from 'react-router';
import { ApolloError } from '@apollo/client';
import { Input } from '../components/Input';
import { FormActions } from '../components/FormActions';
import { PageHeader } from '../components/PageHeader';
import { FileDropZone } from '../components/FileDropZone';
import { FileUploadProgressDialog } from '../components/FileUploadProgressDialog';
import {
  AttachedDrawing,
  AttachedImageList,
  FilePreviewDialog,
  FileRejectMessage,
  useFilePreview,
} from '../components/AttachedFileList';
import { useValidation } from '../utils/Validation';
import { UPLOAD_LIMIT_LABEL, buildRejectMessage } from '../utils/FileUpload.logic';
import { useFormFileUpload } from '../utils/FileUploadSubmit';
import { downloadFile } from '../utils/DownloadFile';
import {
  ExistingImage,
  ImageListItem,
  PendingImage,
  PreviewTarget,
  buildDrawingListItem,
  buildExistingImages,
  buildImageListItems,
  buildStepLabels,
  selectKeptExistingImages,
} from '../utils/AttachedFileList.logic';
import {
  FRAME_IMAGE_ZONE_ACCEPT,
  FRAME_ZONE_ACCEPT,
  classifyFrameFiles,
} from './AdminCreateFramePage.logic';
import {
  useGetFrameLazyQuery,
  useUpdateFrameMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { ConfirmDialog, GraphQLErrorDialog } from '../components/Dialogs';
import * as Const from '../Const';
import { ToastContext } from '../components/Toast';
import './AdminCreateFramePage.css';

/** 図枠一覧のパス。キャンセルと保存の後にこの一覧へ戻る */
const LIST_PATH = '/configs/frames';

// ファイルの送信の失敗の通知は、どのファイルで失敗したかを読み終えられるよう長めに出す（ミリ秒）
const ERROR_TOAST_TIMEOUT = 10000;

/** 名前の検証。先頭と末尾の空白だけの名前も未入力として扱う */
function validateFrameName(value: string): string | undefined {
  if (value.trim() === '') {
    return '必須入力項目です';
  }
  return undefined;
}

const FRAME_RULES = {
  name: validateFrameName,
};

interface AdminCreateFramePageProps {
  existingId?: number;
}

interface FrameFormTexts {
  title: string;
  submitLabel: string;
  completedMessage: string;
  failedMessage: string;
}

/** 新規登録と編集で、見出し・送信ボタン・通知の文言を出し分ける */
function getFormTexts(isNew: boolean): FrameFormTexts {
  if (isNew) {
    return {
      title: '図枠の登録',
      submitLabel: '登録',
      completedMessage: '図枠を登録しました',
      failedMessage: '図枠の登録に失敗しました',
    };
  }
  return {
    title: '図枠の編集',
    submitLabel: '保存',
    completedMessage: '図枠を保存しました',
    failedMessage: '図枠の保存に失敗しました',
  };
}

/**
 * 図枠の登録・編集の画面。図面の登録・編集の画面と同じく、番号付きの段落に沿って、
 * 名前を入れ、図枠ファイルを追加し、図枠ファイルがあるときだけ関連する画像を追加する
 */
export const AdminCreateFramePage: React.FC<AdminCreateFramePageProps> = function (
  props
) {
  const isNew = !props.existingId;
  const texts = getFormTexts(isNew);

  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const validation = useValidation(FRAME_RULES);
  const [frameName, setFrameName] = validation.useState('name', '');
  const [frameType, setFrameType] = React.useState<number>(1);
  const [dxfFile, setDxfFile] = React.useState<File | null>(null);
  const [dxfPreviewUrl, setDxfPreviewUrl] = React.useState<string | null>(null);
  const [existingDrawingName, setExistingDrawingName] = React.useState<string | null>(null);
  const [existingDrawingUrl, setExistingDrawingUrl] = React.useState<string | null>(null);
  const [pendingImages, setPendingImages] = React.useState<PendingImage[]>([]);
  const [existingImages, setExistingImages] = React.useState<ExistingImage[]>([]);
  const [removedExistingImageIds, setRemovedExistingImageIds] = React.useState<number[]>([]);
  const [loadedExistingImageIds, setLoadedExistingImageIds] = React.useState<number[]>([]);
  // 追加欄ごとの、形式や容量で添付できなかったファイルの知らせ。ファイルを入れた追加欄のすぐ下に出す
  const [drawingFileError, setDrawingFileError] = React.useState('');
  const [imageFileError, setImageFileError] = React.useState('');
  const [confirmRemoveDrawingVisible, setConfirmRemoveDrawingVisible] = React.useState(false);
  // 図枠ファイルはDXFだけで縮小表示が無いので、拡大表示で初めて読み終えたときに切り替えるのは関連画像の縮小表示だけ
  const preview = useFilePreview((target: PreviewTarget) => {
    if (target.existingImageId !== null) {
      const existingId = target.existingImageId;
      setLoadedExistingImageIds((prev) => {
        if (prev.includes(existingId)) {
          return prev;
        }
        return prev.concat([existingId]);
      });
    }
  });

  // 送信や保存の失敗は、画面をダイアログに置き換えずに失敗の通知で知らせ、入力と添付を残して再実行できるようにする。
  // 送信を始めるときに前回の失敗を消すためnullが渡されるが、通知は自然に消えるので何もしない
  const notifyUploadFailure = function (message: string | null) {
    if (message !== null) {
      toastContext.showToast(message, 'error', ERROR_TOAST_TIMEOUT);
    }
  };
  const notifySaveFailure = function (error: ApolloError | null) {
    if (error !== null) {
      toastContext.showToast(texts.failedMessage, 'error');
    }
  };
  const { uploading, uploadProgress, registering, submitFiles } = useFormFileUpload(
    'frame',
    notifyUploadFailure,
    notifySaveFailure
  );
  const [getFrame, getFrameResult] = useGetFrameLazyQuery({
    onCompleted(data) {
      const frame = data.getFrame;
      setFrameName(frame.name);
      setFrameType(frame.frame_type);
      setExistingDrawingName(frame.dxf_file_name);
      setExistingDrawingUrl(frame.dxf_url);
      setExistingImages(buildExistingImages(frame.dxf_images));
      setRemovedExistingImageIds([]);
      setLoadedExistingImageIds([]);
    },
  });
  // 成功と失敗の扱いはuseFormFileUploadが担う。onErrorを渡すと失敗しても例外が投げられず、保存できたものとして一覧へ戻ってしまうため渡さない
  const [updateFrame] = useUpdateFrameMutation();

  // 画面を離れるときに、手元のファイルを指すために作ったアドレスを解放する
  const pendingImagesRef = React.useRef<PendingImage[]>([]);
  const dxfPreviewUrlRef = React.useRef<string | null>(null);
  pendingImagesRef.current = pendingImages;
  dxfPreviewUrlRef.current = dxfPreviewUrl;
  React.useEffect(() => {
    return () => {
      pendingImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      if (dxfPreviewUrlRef.current !== null) {
        URL.revokeObjectURL(dxfPreviewUrlRef.current);
      }
    };
  }, []);

  if (props.existingId) {
    if (!getFrameResult.called) {
      getFrame({ variables: { frameId: props.existingId } });
    }

    if (getFrameResult.error) {
      return <GraphQLErrorDialog error={getFrameResult.error} />;
    }

    if (getFrameResult.loading || !getFrameResult.data) {
      return <Loading />;
    }
  }

  const hasDrawing = dxfFile !== null || existingDrawingName !== null;
  const canSubmit = hasDrawing && !validation.isInvalid && !uploading && !confirmRemoveDrawingVisible;
  const drawingListItem = buildDrawingListItem(
    dxfFile,
    dxfPreviewUrl,
    existingDrawingName,
    existingDrawingUrl,
    Const.API_URL,
    false
  );
  const imageListItems = buildImageListItems(
    existingImages,
    removedExistingImageIds,
    loadedExistingImageIds,
    pendingImages,
    Const.API_URL
  );
  const keptExistingImages = selectKeptExistingImages(existingImages, removedExistingImageIds);
  const stepLabels = buildStepLabels([
    { key: 'name', title: '名前', visible: true },
    { key: 'drawing', title: `図枠ファイルを追加 (DXF、${UPLOAD_LIMIT_LABEL})`, visible: true },
    {
      key: 'images',
      title: `図枠ファイルに関連する画像がある場合は追加 (任意、${UPLOAD_LIMIT_LABEL})`,
      visible: hasDrawing,
    },
  ]);
  // 送信の進捗か保存中を知らせるダイアログを出している間は、読み込み中の表示を重ねない
  const progressDialogOpen = registering || uploadProgress !== null;

  const clearPendingImages = () => {
    pendingImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    setPendingImages([]);
  };

  const appendPendingImages = (files: File[]) => {
    const added = files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPendingImages((prev) => prev.concat(added));
  };

  const handleDrawingZoneFiles = (files: File[]) => {
    if (uploading || hasDrawing) {
      return;
    }

    const classification = classifyFrameFiles(files, false);
    if (classification.frame !== null) {
      if (dxfPreviewUrl !== null) {
        URL.revokeObjectURL(dxfPreviewUrl);
      }
      setDxfFile(classification.frame);
      setDxfPreviewUrl(URL.createObjectURL(classification.frame));
    }
    if (classification.images.length > 0) {
      appendPendingImages(classification.images);
    }
    // 入れ直したときは前回の知らせを今回の結果で置き換え、すべて添付できたときは知らせを消す
    setDrawingFileError(buildRejectMessage(classification.rejected));
  };

  const handleImageZoneFiles = (files: File[]) => {
    if (uploading || !hasDrawing) {
      return;
    }

    const classification = classifyFrameFiles(files, true);
    if (classification.images.length > 0) {
      appendPendingImages(classification.images);
    }
    // 入れ直したときは前回の知らせを今回の結果で置き換え、すべて添付できたときは知らせを消す
    setImageFileError(buildRejectMessage(classification.rejected));
  };

  const requestRemoveDrawing = () => {
    if (uploading) {
      return;
    }

    setConfirmRemoveDrawingVisible(true);
  };

  // 図枠ファイルを消すと、関連画像も一緒に外す。登録済みの関連画像は保存したときに削除される
  const removeDrawing = () => {
    setConfirmRemoveDrawingVisible(false);
    if (uploading) {
      return;
    }

    preview.close();
    if (dxfPreviewUrl !== null) {
      URL.revokeObjectURL(dxfPreviewUrl);
    }
    setDxfFile(null);
    setDxfPreviewUrl(null);
    setExistingDrawingName(null);
    setExistingDrawingUrl(null);
    clearPendingImages();
    setExistingImages([]);
    setRemovedExistingImageIds([]);
    setLoadedExistingImageIds([]);
    // 図枠ファイルを外すと追加欄が選び直しの状態に戻るので、前に添付できなかったファイルの知らせも消す
    setDrawingFileError('');
    setImageFileError('');
  };

  const removeImage = (item: ImageListItem) => {
    if (uploading) {
      return;
    }

    if (preview.target !== null && preview.target.src === item.previewSrc) {
      preview.close();
    }
    if (item.existingId !== null) {
      const existingId = item.existingId;
      setRemovedExistingImageIds((prev) => prev.concat([existingId]));
    }
    if (item.previewUrl !== null) {
      URL.revokeObjectURL(item.previewUrl);
      setPendingImages((prev) => prev.filter((image) => image.previewUrl !== item.previewUrl));
    }
  };

  // 削除予定にした登録済みの画像を元に戻す
  const restoreImage = (item: ImageListItem) => {
    if (uploading) {
      return;
    }

    if (item.existingId !== null) {
      setRemovedExistingImageIds((prev) => prev.filter((id) => id !== item.existingId));
    }
  };

  const openImagePreview = (item: ImageListItem) => {
    preview.open({
      src: item.previewSrc,
      fileName: item.fileName,
      downloadUrl: item.downloadUrl,
      existingImageId: item.existingId,
      isDrawing: false,
      thumbnailReady: item.thumbnailReady,
    });
  };

  // 添付済みのファイルを順に送信してから図枠を保存する。失敗しても入力と添付は残し、再実行できる。
  // 同じ画面の中で送信済みのファイルは、保存だけ失敗して再実行したときに送り直さない
  const submit = async function () {
    if (!canSubmit) {
      return;
    }
    await submitFiles(
      dxfFile,
      pendingImages.map((image) => image.file),
      async (uploaded) => {
        await updateFrame({
          variables: {
            input: {
              id: props.existingId,
              name: frameName,
              frame_type: frameType,
              dxf: uploaded.dxf,
              dxf_images: uploaded.dxfImages,
              uploaded_dxf_images: keptExistingImages.map((image) => image.id),
            },
          },
        });
      },
      () => {
        toastContext.showToast(texts.completedMessage, 'success');
        history.push(LIST_PATH);
      }
    );
  };

  return (
    <div className="AdminCreateFramePage">
      <FileUploadProgressDialog progress={uploadProgress} registering={registering} />
      <Loading open={uploading && !progressDialogOpen} />
      <FilePreviewDialog preview={preview} />
      <ConfirmDialog
        open={confirmRemoveDrawingVisible}
        title="図枠ファイルの削除"
        message={<p>削除時は関連画像も失われますがよろしいですか？</p>}
        yesLabel="削除"
        danger={true}
        onYes={removeDrawing}
        onNo={() => setConfirmRemoveDrawingVisible(false)}
      />
      <PageHeader
        title={texts.title}
        breadcrumbs={[
          { label: '共通設定' },
          { label: '図枠設定', to: LIST_PATH },
          { label: texts.title },
        ]}
      />
      <p>{stepLabels.name}</p>
      <Input
        className="AdminCreateFramePage-name_input"
        type="text"
        value={frameName}
        disabled={uploading}
        onChange={(event) => setFrameName(event.target.value)}
        validationMessage={validation.get('name')}
      />
      {/*
      <p>図枠種類</p>
      <select
        value={frameType}
        className="App-input-container"
        onChange={(event) => setFrameType(parseInt(event.target.value))}
      >
        {master.Frames.map((frame) => (
          <option value={frame.id}>{frame.name}</option>
        ))}
      </select>
      */}
      <p>{stepLabels.drawing}</p>
      {!hasDrawing && (
        <React.Fragment>
          <p>図枠ファイル・関連画像を複数ドラッグ&ドロップすることで一括登録が可能です。</p>
          <FileDropZone
            accept={FRAME_ZONE_ACCEPT}
            multiple={true}
            disabled={uploading}
            mainLabel="ここにDXFファイル・画像ファイルをドラッグ&ドロップ"
            subLabel="クリックでファイルを選択"
            onFilesSelected={handleDrawingZoneFiles}
          />
        </React.Fragment>
      )}
      {drawingListItem !== null && (
        <AttachedDrawing
          item={drawingListItem}
          disabled={uploading}
          onPreview={() => undefined}
          onDownload={(item) => downloadFile(item.downloadUrl, item.fileName)}
          onRemove={requestRemoveDrawing}
        />
      )}
      <FileRejectMessage message={drawingFileError} />
      {hasDrawing && (
        <React.Fragment>
          <p>{stepLabels.images}</p>
          <FileDropZone
            accept={FRAME_IMAGE_ZONE_ACCEPT}
            multiple={true}
            disabled={uploading}
            mainLabel="ここに画像ファイルをドラッグ&ドロップ"
            subLabel="クリックでファイルを選択"
            onFilesSelected={handleImageZoneFiles}
          />
          <FileRejectMessage message={imageFileError} />
          <AttachedImageList
            items={imageListItems}
            disabled={uploading}
            onPreview={openImagePreview}
            onRemove={removeImage}
            onRestore={restoreImage}
          />
        </React.Fragment>
      )}
      <FormActions
        submitLabel={texts.submitLabel}
        submitDisabled={!canSubmit}
        onSubmit={() => {
          submit();
        }}
        onCancel={() => history.push(LIST_PATH)}
      />
    </div>
  );
};
